import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import type { App } from "../types/app";
import type { SmartLink } from "../types/smartLink";
import { isSupportedStoreUrl } from "../utils/smartLink";

const SMART_LINKS_COLLECTION = "smartLinks";
const SMART_LINK_OWNERS_COLLECTION = "smartLinkOwners";
const SLUG_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

const timestampToDate = (value: unknown): Date => {
  if (value instanceof Timestamp) return value.toDate();
  if (value && typeof (value as any).toDate === "function") {
    return (value as any).toDate();
  }
  return value ? new Date(value as string) : new Date();
};

const toSmartLink = (slug: string, data: any): SmartLink => ({
  slug,
  appId: String(data.appId || ""),
  appName: String(data.appName || "App"),
  androidStoreUrl: String(data.androidStoreUrl || ""),
  iosStoreUrl: String(data.iosStoreUrl || ""),
  userId: String(data.userId || ""),
  createdAt: timestampToDate(data.createdAt),
  updatedAt: timestampToDate(data.updatedAt),
});

class SmartLinkService {
  private generateSlug(length = 10): string {
    const values = new Uint8Array(length);
    window.crypto.getRandomValues(values);
    return Array.from(values, (value) => SLUG_ALPHABET[value % SLUG_ALPHABET.length]).join("");
  }

  private getStoreUrls(app: App) {
    const androidStoreUrl = String(
      app.androidStoreUrl || (app.platform === "android" ? app.storeUrl : ""),
    ).trim();
    const iosStoreUrl = String(
      app.iosStoreUrl || (app.platform === "ios" ? app.storeUrl : ""),
    ).trim();

    if (!androidStoreUrl && !iosStoreUrl) {
      throw new Error("Add an App Store or Google Play URL to this app first.");
    }
    if (androidStoreUrl && !isSupportedStoreUrl(androidStoreUrl)) {
      throw new Error("The registered Google Play URL is not valid.");
    }
    if (iosStoreUrl && !isSupportedStoreUrl(iosStoreUrl)) {
      throw new Error("The registered App Store URL is not valid.");
    }

    return { androidStoreUrl, iosStoreUrl };
  }

  async getUserLinks(userId: string): Promise<SmartLink[]> {
    const ownerSnapshot = await getDocs(
      query(
        collection(db, SMART_LINK_OWNERS_COLLECTION),
        where("userId", "==", userId),
      ),
    );
    return Promise.all(
      ownerSnapshot.docs.map(async (ownerDoc) => {
        const publicSnapshot = await getDoc(
          doc(db, SMART_LINKS_COLLECTION, ownerDoc.id),
        );
        const ownerData = ownerDoc.data();
        return toSmartLink(ownerDoc.id, {
          ...(publicSnapshot.exists() ? publicSnapshot.data() : {}),
          ...ownerData,
        });
      }),
    );
  }

  async getPublicLink(slug: string): Promise<SmartLink | null> {
    const snapshot = await getDoc(doc(db, SMART_LINKS_COLLECTION, slug));
    return snapshot.exists() ? toSmartLink(snapshot.id, snapshot.data()) : null;
  }

  async saveLink(app: App, userId: string, existingSlug?: string): Promise<SmartLink> {
    const { androidStoreUrl, iosStoreUrl } = this.getStoreUrls(app);
    let slug = existingSlug;

    if (!slug) {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const candidate = this.generateSlug();
        const candidateSnapshot = await getDoc(
          doc(db, SMART_LINKS_COLLECTION, candidate),
        );
        if (!candidateSnapshot.exists()) {
          slug = candidate;
          break;
        }
      }
    }

    if (!slug) throw new Error("Could not create a unique link. Please try again.");

    const linkRef = doc(db, SMART_LINKS_COLLECTION, slug);
    const ownerRef = doc(db, SMART_LINK_OWNERS_COLLECTION, slug);
    const existingOwnerSnapshot = await getDoc(ownerRef);
    const now = serverTimestamp();
    const batch = writeBatch(db);
    // Keep ownership and internal app identifiers in a separate private doc.
    // The public destination doc contains only what the landing page displays.
    batch.set(linkRef, {
      slug,
      appName: app.name,
      androidStoreUrl,
      iosStoreUrl,
    });
    batch.set(ownerRef, {
      slug,
      appId: app.appId,
      userId,
      createdAt: existingOwnerSnapshot.exists()
        ? existingOwnerSnapshot.data().createdAt || now
        : now,
      updatedAt: now,
    });
    await batch.commit();

    return {
      slug,
      appId: app.appId,
      appName: app.name,
      androidStoreUrl,
      iosStoreUrl,
      userId,
      createdAt: existingOwnerSnapshot.exists()
        ? timestampToDate(existingOwnerSnapshot.data().createdAt)
        : new Date(),
      updatedAt: new Date(),
    };
  }
}

const smartLinkService = new SmartLinkService();

export default smartLinkService;
